import React from 'react';
import { LayoutDashboard, Rocket, History, User, BarChart3, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BottomNavProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  current_user: {
    is_admin?: boolean;
    is_active?: boolean;
  };
}

export const BottomNav: React.FC<BottomNavProps> = ({ activeTab, setActiveTab, current_user }) => {
  const isSettingsActive = activeTab === 'recommend' || activeTab === 'simple' || activeTab === 'detail';
  const isBotsActive = activeTab === 'bots';
  const isDcaPage = window.location.pathname.startsWith('/dca');
  const isSignalPage = window.location.pathname.startsWith('/signal');
  const isSignalIndex = window.location.pathname === '/signal' || window.location.pathname === '/signal/';

  // 모바일 하단바에서 현재 활성화된 탭 파악을 위한 헬퍼
  const isHistoryActive = isDcaPage ? activeTab === 'history' : (isSignalIndex ? window.location.hash === '#history' : false);
  const isPerformanceActive = isDcaPage ? activeTab === 'performance' : (isSignalIndex ? window.location.hash === '#daily_stats' : false);
  const isStriderPage = window.location.pathname.startsWith('/strider');

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-xl border-t border-border px-6 py-3 flex justify-between items-center z-50 max-w-md mx-auto shadow-[0_-4px_20px_rgba(0,0,0,0.03)] rounded-t-3xl md:hidden transition-colors duration-300">
      <button 
        onClick={() => {
          if (isDcaPage) {
            setActiveTab('bots');
          } else {
            window.location.href = '/dca#bots';
          }
        }}
        className={`flex flex-col items-center gap-1 transition-all ${isBotsActive && isDcaPage ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${isBotsActive && isDcaPage ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <Rocket className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">DCA 봇</span>
      </button>

      <button 
        onClick={() => {
          if (isSignalIndex) {
            window.location.hash = '#bots';
          } else {
            window.location.href = '/signal#bots';
          }
        }}
        className={`flex flex-col items-center gap-1 transition-all ${(isSignalIndex && (window.location.hash === '#bots' || !window.location.hash)) || (isSignalPage && !isSignalIndex) ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${(isSignalIndex && (window.location.hash === '#bots' || !window.location.hash)) || (isSignalPage && !isSignalIndex) ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <Zap className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">시그널 봇</span>
      </button>

      <button 
        onClick={() => {
          window.location.href = '/strider';
        }}
        className={`flex flex-col items-center gap-1 transition-all ${isStriderPage ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${isStriderPage ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <Shield className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">Strider</span>
      </button>

      <button 
        onClick={() => {
          if (isDcaPage) {
            setActiveTab('performance');
          } else if (isSignalIndex) {
            window.location.hash = '#daily_stats';
          } else {
            window.location.href = '/signal#daily_stats';
          }
        }}
        className={`flex flex-col items-center gap-1 transition-all ${isPerformanceActive ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${isPerformanceActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <BarChart3 className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">성과 분석</span>
      </button>

      <button 
        onClick={() => {
          if (isDcaPage) {
            setActiveTab('history');
          } else if (isSignalIndex) {
            window.location.hash = '#history';
          } else {
            window.location.href = '/signal#history';
          }
        }}
        className={`flex flex-col items-center gap-1 transition-all ${isHistoryActive ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${isHistoryActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <History className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">거래 내역</span>
      </button>

      <button 
        onClick={() => {
          if (isSignalPage) {
            window.location.href = '/dca#profile';
          } else {
            setActiveTab('profile');
          }
        }}
        className={`flex flex-col items-center gap-1 transition-all ${activeTab === 'profile' ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <div className={`p-1.5 rounded-xl transition-all ${activeTab === 'profile' ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : ''}`}>
          <User className="w-5 h-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-tighter">내 계정</span>
      </button>
    </div>
  );
};

export const DesktopNav: React.FC<BottomNavProps> = ({ activeTab, setActiveTab, current_user }) => {
  const isBotsActive = activeTab === 'bots';
  const isDcaPage = window.location.pathname.startsWith('/dca');
  const isSignalPage = window.location.pathname.startsWith('/signal');
  const isSignalIndex = window.location.pathname === '/signal' || window.location.pathname === '/signal/';
  const isStriderPage = window.location.pathname.startsWith('/strider');

  const isHistoryActive = isDcaPage ? activeTab === 'history' : (isSignalIndex ? window.location.hash === '#history' : false);
  const isPerformanceActive = isDcaPage ? activeTab === 'performance' : (isSignalIndex ? window.location.hash === '#daily_stats' : false);

  return (
    <nav className="hidden md:flex bg-muted/20 backdrop-blur-md border border-border/50 p-1 rounded-2xl gap-1 shadow-sm transition-colors duration-300">
      <Button 
        variant={isBotsActive && isDcaPage ? "default" : "ghost"}
        onClick={() => {
          if (isDcaPage) setActiveTab('bots');
          else window.location.href = '/dca#bots';
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${isBotsActive && isDcaPage ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Rocket className="w-4 h-4" /> DCA 봇
      </Button>

      <Button 
        variant={((isSignalIndex && (window.location.hash === '#bots' || !window.location.hash)) || (isSignalPage && !isSignalIndex)) && !isStriderPage ? "default" : "ghost"}
        onClick={() => {
          if (isSignalIndex) window.location.hash = '#bots';
          else window.location.href = '/signal#bots';
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${((isSignalIndex && (window.location.hash === '#bots' || !window.location.hash)) || (isSignalPage && !isSignalIndex)) && !isStriderPage ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Zap className="w-4 h-4" /> 시그널 봇
      </Button>

      <Button 
        variant={isStriderPage ? "default" : "ghost"}
        onClick={() => {
          window.location.href = '/strider';
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${isStriderPage ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <Shield className="w-4 h-4" /> 묶음전략 봇
      </Button>

      <Button 
        variant={isHistoryActive ? "default" : "ghost"}
        onClick={() => {
          if (isDcaPage) {
            setActiveTab('history');
          } else if (isSignalIndex) {
            window.location.hash = '#history';
          } else {
            window.location.href = '/signal#history';
          }
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${isHistoryActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <History className="w-4 h-4" /> 거래 내역
      </Button>

      <Button 
        variant={isPerformanceActive ? "default" : "ghost"}
        onClick={() => {
          if (isDcaPage) {
            setActiveTab('performance');
          } else if (isSignalIndex) {
            window.location.hash = '#daily_stats';
          } else {
            window.location.href = '/signal#daily_stats';
          }
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${isPerformanceActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <BarChart3 className="w-4 h-4" /> 성과 분석
      </Button>

      <Button 
        variant={activeTab === 'profile' ? "default" : "ghost"}
        onClick={() => {
          if (isSignalPage) {
            window.location.href = '/dca#profile';
          } else {
            setActiveTab('profile');
          }
        }}
        className={`rounded-xl px-3.5 h-9 font-bold flex items-center gap-1.5 text-sm ${activeTab === 'profile' ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-muted-foreground hover:text-foreground'}`}
      >
        <User className="w-4 h-4" /> 내 계정
      </Button>
    </nav>
  );
};
